/**
 * SchedaSchermate — le tre schermate fuori dalle domande: l'apertura, l'ultima schermata con i
 * contatti, la conferma. Ognuna si accende o si spegne; spenta, la pagina pubblica usa le sue
 * parole (l'apertura ripiega su titolo e descrizione del questionario).
 */

import React from 'react';
import { Box, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';
import { tokens } from '../../../theme';
import { CampoTesto } from './CampoTesto';
import { EditorContatti } from './EditorContatti';
import { aperturaVuota, confermaVuota, contattiVuoti, type Bozza } from './bozza';

interface Props {
  bozza: Bozza;
  onChange: (patch: Partial<Bozza>) => void;
}

const Riquadro: React.FC<{
  titolo: string;
  spiegazione: string;
  acceso: boolean;
  onAccendi: (acceso: boolean) => void;
  children: React.ReactNode;
}> = ({ titolo, spiegazione, acceso, onAccendi, children }) => (
  <Paper variant="outlined" sx={{ p: 2, borderColor: tokens.color.line }}>
    <FormControlLabel
      control={<Switch checked={acceso} onChange={(e) => onAccendi(e.target.checked)} />}
      label={(
        <Box>
          <Typography variant="body2" fontWeight={700}>{titolo}</Typography>
          <Typography variant="caption" sx={{ color: tokens.color.ash }}>{spiegazione}</Typography>
        </Box>
      )}
    />
    {acceso && <Box sx={{ mt: 1.5 }}>{children}</Box>}
  </Paper>
);

export const SchedaSchermate: React.FC<Props> = ({ bozza, onChange }) => {
  const { apertura, contatti, conferma } = bozza;
  return (
    <Stack spacing={2}>
      <Riquadro
        titolo="Apertura"
        spiegazione="La prima schermata. Spenta, si usano titolo e descrizione del questionario."
        acceso={!!apertura}
        onAccendi={(a) => onChange({ apertura: a ? aperturaVuota(bozza.titolo) : null })}
      >
        {apertura && (
          <Stack spacing={1.5}>
            <CampoTesto label="Titolo" value={apertura.titolo} onChange={(v) => onChange({ apertura: { ...apertura, titolo: v } })} helperText="Es. «Da marzo 2027»" />
            <CampoTesto label="Testo" value={apertura.testo} onChange={(v) => onChange({ apertura: { ...apertura, testo: v } })} multiline minRows={2} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <CampoTesto label="Etichetta piccola" value={apertura.etichetta} onChange={(v) => onChange({ apertura: { ...apertura, etichetta: v } })} helperText="Es. «7 domande, poi i tuoi dati»" />
              <CampoTesto label="Bottone" value={apertura.bottone} onChange={(v) => onChange({ apertura: { ...apertura, bottone: v } })} helperText="Vuoto = «Inizia»" />
            </Stack>
          </Stack>
        )}
      </Riquadro>

      <Riquadro
        titolo="Ultima schermata: i contatti"
        spiegazione="Chi è chi risponde. Senza, le risposte da un link generico restano anonime."
        acceso={!!contatti}
        onAccendi={(a) => onChange({ contatti: a ? contattiVuoti() : null })}
      >
        {contatti && <EditorContatti contatti={contatti} onChange={(c) => onChange({ contatti: c })} />}
      </Riquadro>

      <Riquadro
        titolo="Conferma"
        spiegazione="La schermata dopo l'invio. Spenta, la pagina mostra un ringraziamento semplice."
        acceso={!!conferma}
        onAccendi={(a) => onChange({ conferma: a ? confermaVuota() : null })}
      >
        {conferma && (
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <CampoTesto label="Scritta grande" value={conferma.in_grande} onChange={(v) => onChange({ conferma: { ...conferma, in_grande: v } })} helperText="Es. «Da marzo 2027»" />
              <CampoTesto label="Titolo" value={conferma.titolo} onChange={(v) => onChange({ conferma: { ...conferma, titolo: v } })} />
            </Stack>
            <CampoTesto label="Testo" value={conferma.testo} onChange={(v) => onChange({ conferma: { ...conferma, testo: v } })} multiline minRows={2} />
            <CampoTesto
              label="Testo in più per chi ha chiesto gli aggiornamenti"
              value={conferma.testo_se_consenso}
              onChange={(v) => onChange({ conferma: { ...conferma, testo_se_consenso: v } })}
              multiline
              helperText="Gli aggiornamenti partono solo dopo il clic nell'email: qui si dice di aprirla"
            />
            <Typography variant="caption" sx={{ color: tokens.color.ash, fontWeight: 600 }}>Collegamento in fondo (facoltativo)</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <CampoTesto
                label="Frase prima"
                value={conferma.collegamento?.prima}
                onChange={(v) => onChange({ conferma: { ...conferma, collegamento: { prima: v, etichetta: conferma.collegamento?.etichetta ?? '', url: conferma.collegamento?.url ?? '' } } })}
              />
              <CampoTesto
                label="Testo del collegamento"
                value={conferma.collegamento?.etichetta}
                onChange={(v) => onChange({ conferma: { ...conferma, collegamento: { prima: conferma.collegamento?.prima ?? '', etichetta: v, url: conferma.collegamento?.url ?? '' } } })}
              />
              <CampoTesto
                label="Indirizzo"
                value={conferma.collegamento?.url}
                placeholder="https://…"
                onChange={(v) => onChange({ conferma: { ...conferma, collegamento: { prima: conferma.collegamento?.prima ?? '', etichetta: conferma.collegamento?.etichetta ?? '', url: v } } })}
              />
            </Stack>
          </Stack>
        )}
      </Riquadro>
    </Stack>
  );
};
